import React, { ChangeEventHandler, MouseEventHandler, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate, useParams } from 'react-router-dom';

import { RadioOrSelectOption } from '../../../../../types/common.types';
import { HashTag, Member, MemberProfile, Post } from '../../../../../types/entity.types';

import ProfilePicture from '../../../../common/profile-picture/ProfilePicture';
import TribeFeedWrapper from '../feed/wrapper/TribeFeedWrapper';
import TribeInfo from '../info/TribeInfo';

import BadgesContainer from '../../../../../containers/badges/Badges.container';
import TribePostContainer from '../../../../../containers/programme/tribe/tribe-post/TribePost.container';

import { ReactComponent as ArrowIcon } from '../../../../../resources/icons/arrow-single.svg';

import './tribe-profile.scss';

type Props = {
  profile?: MemberProfile;
  profilePicture?: string;
  status: string;
  posts: Post[];
  members: Member[];
  searchType?: RadioOrSelectOption;
  onSearchTypeChange: (option: RadioOrSelectOption) => void;
  filterValue: string;
  onFilterValueChange: ChangeEventHandler<HTMLInputElement>;
  refreshPost: (postExternalReference: string) => void;
  hashtags?: HashTag[];
  setComposing: (value: boolean) => void;
  loadPosts: () => void;
  onSearchClick: MouseEventHandler<HTMLButtonElement>;
  pendingApprovals?: number;
};

const TribeProfile = ({
  profile,
  status,
  posts,
  members,
  searchType,
  onSearchTypeChange,
  filterValue,
  onFilterValueChange,
  refreshPost,
  hashtags,
  setComposing,
  loadPosts,
  pendingApprovals,
  onSearchClick
}: Props) => {
  const navigate = useNavigate();

  const { memberExternalReference } = useParams();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [memberExternalReference]);

  const renderSubheading = () => {
    if (!profile) {
      return <></>;
    }

    const { teamNames, admin } = profile;
    const joinedTeamName = teamNames?.join(' | ');

    if (joinedTeamName && admin) {
      return <p>{joinedTeamName} | Admin</p>;
    } else if (joinedTeamName) {
      return <p>{joinedTeamName}</p>;
    } else if (admin) {
      return <p>Admin</p>;
    }

    return <></>;
  };

  return (
    <div className="tribe-profile">
      <div className="tribe-profile__header">
        <ArrowIcon
          className="tribe-profile__header__arrow-icon"
          onClick={() => navigate(-1)}
          data-testid="arrow-icon"
        />
        <h1>Profile</h1>
      </div>

      <div className="tribe-profile__user">
        <ProfilePicture src={profile?.profilePicture} />
        <div className="tribe-profile__user__info">
          <div className="tribe-profile__user__info__name-badges">
            <p className="tribe-profile__username">{profile?.username}</p>
            <BadgesContainer
              memberReference={memberExternalReference}
              badgeReferences={profile?.memberBadgeIconExternalReferences ?? []}
            />
          </div>
          {renderSubheading()}
        </div>
      </div>

      <div className="tribe-profile__content">
        <TribeFeedWrapper
          status={status}
          onSearchClick={onSearchClick}
          searchType={searchType}
          onSearchTypeChange={onSearchTypeChange}
          filterValue={filterValue}
          onFilterValueChange={onFilterValueChange}
        >
          <div
            className="tribe-profile__posts"
            ref={scrollRef}
            id="tribe-profile-posts"
            data-testid="tribe-profile-posts"
          >
            {posts.length > 0 && (
              <InfiniteScroll
                dataLength={posts.length}
                next={loadPosts}
                scrollableTarget="tribe-profile-posts"
                loader={<></>}
                hasMore
              >
                {posts.map((post) => (
                  <TribePostContainer
                    key={post.externalReference}
                    status={status}
                    post={post}
                    hashtags={hashtags}
                    refreshPost={refreshPost}
                    setComposing={setComposing}
                    searchType={searchType}
                  />
                ))}
              </InfiniteScroll>
            )}
            {posts.length === 0 && (
              <div className="tribe-profile__posts--empty">
                <p>No Posts</p>
              </div>
            )}
          </div>
        </TribeFeedWrapper>
        <TribeInfo status={status} pendingApprovals={pendingApprovals} members={members} />
      </div>
    </div>
  );
};

export default TribeProfile;
